import { Clock, Coffee, MapPin, ShoppingCart } from "lucide-react";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useDispatch } from "react-redux";
import { addItem } from "../../Redux/State-slice/CartSlice";
import useDistanceLabel from "../../customHooks/useDistanceLabel";
import { FOOD_TYPE, FOOD_TYPES, FOOD_TYPE_LABELS, foodTimeLeftMs } from "../../services/normalizers";

const labels = {
  country: "Bangladesh",
  currency: {
    bdt: { symbol: "৳" },
    usa: { symbol: "$" },
  },
};

function FoodCountdown({ food }) {
  const [timeLeft, setTimeLeft] = useState(null);

  function formatTimeLeft(diffMs) {
    if (diffMs === null) return null;
    if (diffMs <= 0) return "00:00";
    const totalMinutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${String(hours).padStart(2, "0")}H ${String(minutes).padStart(2, "0")} Min`;
  }

  useEffect(() => {
    const update = () => {
      setTimeLeft(formatTimeLeft(foodTimeLeftMs(food)));
    };
    update();
    const interval = setInterval(update, 60 * 1000);
    return () => clearInterval(interval);
  }, [food]);

  return timeLeft ? <span>{timeLeft}</span> : null;
}

export default function FoodCard({ item }) {
  const [hovered, setHovered] = useState(false);
  const dispatch = useDispatch();
  const distanceLabel = useDistanceLabel(item?.location?.lat, item?.location?.long);
  const handleAddToCart = (food) => {
    dispatch(addItem(food));
  };

  const currencySymbol =
    labels.country === "Bangladesh"
      ? labels.currency.bdt.symbol
      : labels.currency.usa.symbol;

  const discountedPrice = (
    item?.price -
    (item?.discountType === "percentage"
      ? item?.price * (item?.discountValue / 100)
      : item?.discountValue || 0)
  )?.toFixed(0);

  return (
    <motion.div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      transition={{ duration: 0.25 }}
      style={{
        overflow: "hidden",
        border: "none",
        backgroundColor: "#fff",
        cursor: "pointer",
        borderRadius: 8,
      }}
    >
      <div className="d-flex" style={{ minHeight: 0 }}>
        {/* Vertical seller label */}
        <a
          href={`/SellerProfile/${item?.sellerId?._id}`}
          className="text-decoration-none"
          style={{
            writingMode: "vertical-rl",
            transform: "rotate(180deg)",
            backgroundColor: "#f4f5f7",
            padding: "10px 5px",
            fontWeight: 700,
            fontSize: 11,
            fontFamily: "'Inter', sans-serif",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            letterSpacing: "0.03em",
            color: "#374151",
            flexShrink: 0,
            minWidth: 24,
          }}
        >
          {item?.sellerId?.name || "Seller"}
        </a>

        {/* Image section */}
        <div style={{ position: "relative", overflow: "hidden", flex: 1 }}>
          <motion.img
            src={item?.image || "/placeholder.svg"}
            alt={item?.name}
            animate={{ scale: hovered ? 1.06 : 1 }}
            transition={{ type: "spring", stiffness: 280, damping: 22 }}
            style={{
              width: "100%",
              height: 150,
              objectFit: "cover",
              display: "block",
            }}
          />

          {/* Discount badge */}
          {!!item?.discountValue && (
            <div
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                backgroundColor: "#0d6efd",
                borderRadius: "0 0 0 6px",
                padding: "3px 8px",
              }}
            >
              <span
                style={{
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 11,
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                {item?.discountValue}
                {item?.discountType === "percentage" ? "% OFF" : "TK OFF"}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Footer info */}
      <div
        style={{
          backgroundColor: "#f9fafb",
          padding: "8px 10px 6px",
        }}
      >
        <div className="d-flex align-items-center" style={{ gap: 10 }}>
          {/* Kitchen avatar */}
          <motion.div
            animate={{ scale: hovered ? 1.08 : 1 }}
            transition={{ type: "spring", stiffness: 350, damping: 18 }}
            style={{
              width: 38,
              height: 38,
              borderRadius: "50%",
              overflow: "hidden",
              backgroundColor: "#e9ecef",
              flexShrink: 0,
              border: "2px solid #fff",
              boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            }}
          >
            <img
              src={item?.sellerId?.kitchenLogo || "/placeholder.svg"}
              alt={item?.sellerId?.name}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </motion.div>

          {/* Food info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <a
              href={`/food-details/${item?._id}`}
              className="text-decoration-none"
              style={{ display: "block" }}
            >
              <h6
                style={{
                  fontSize: 13,
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  marginBottom: 2,
                  fontWeight: 700,
                  fontFamily: "'Inter', sans-serif",
                  color: "#1f2937",
                  lineHeight: 1.3,
                }}
              >
                {item?.name || "Food Name"}
              </h6>
            </a>

            {distanceLabel && (
              <div
                className="d-flex align-items-center"
                style={{
                  gap: 3,
                  fontSize: 10,
                  color: "#9ca3af",
                  marginBottom: 2,
                }}
              >
                <MapPin size={10} />
                {distanceLabel}
              </div>
            )}

            <div className="d-flex align-items-center justify-content-between">
              <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                <span
                  style={{
                    fontWeight: 800,
                    color: "#111",
                    fontSize: 15,
                    fontFamily: "'Inter', sans-serif",
                    lineHeight: 1,
                  }}
                >
                  {currencySymbol}
                  {discountedPrice}
                </span>

                {item?.discountValue > 0 && (
                  <span
                    style={{
                      color: "#9ca3af",
                      textDecoration: "line-through",
                      fontWeight: 500,
                      fontSize: 11,
                    }}
                  >
                    {currencySymbol}
                    {item?.price?.toFixed(0)}
                  </span>
                )}
              </div>

              {/* Cart button */}
              <motion.button
                whileHover={{ backgroundColor: "#1b6dc1", scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => handleAddToCart(item)}
                className="d-flex justify-content-center align-items-center border-0"
                style={{
                  backgroundColor: "#111",
                  borderRadius: "50%",
                  width: 30,
                  height: 30,
                  color: "#fff",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                <ShoppingCart size={14} />
              </motion.button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom action buttons */}
      {FOOD_TYPES.includes(item?.type) && (
        <motion.button
          whileHover={{ filter: "brightness(1.1)" }}
          whileTap={{ scale: 0.98 }}
          onClick={() => handleAddToCart(item)}
          className="w-100 border-0 d-flex align-items-center justify-content-between"
          style={{
            backgroundColor:
              item?.type === FOOD_TYPE.PRE_ORDER
                ? "#ffc107"
                : item?.type === FOOD_TYPE.READY_TO_EAT
                  ? "#0d6efd"
                  : "#243142",
            padding: "6px 10px",
            fontWeight: 600,
            fontSize: 12,
            color:
              item?.type === FOOD_TYPE.PRE_ORDER
                ? "#000"
                : item?.type === FOOD_TYPE.READY_TO_EAT
                  ? "#FFD504"
                  : "#fff",
            borderRadius: 0,
            fontFamily: "'Inter', sans-serif",
            cursor: "pointer",
          }}
        >
          {item?.type === FOOD_TYPE.READY_TO_EAT && (
            <>
              <span>{FOOD_TYPE_LABELS[FOOD_TYPE.READY_TO_EAT]}</span>
              <div className="d-flex align-items-center gap-1">
                <Clock size={13} />
                <FoodCountdown food={item} />
              </div>
            </>
          )}

          {item?.type === FOOD_TYPE.E_SHOP && <span>{FOOD_TYPE_LABELS[FOOD_TYPE.E_SHOP]}</span>}

          {item?.type === FOOD_TYPE.PRE_ORDER && (
            <>
              <span>{FOOD_TYPE_LABELS[FOOD_TYPE.PRE_ORDER]}</span>
              <div className="d-flex align-items-center gap-1">
                <Coffee size={13} />
                <span style={{ fontSize: 11 }}>{item?.waitingTime}</span>
              </div>
            </>
          )}
        </motion.button>
      )}
    </motion.div>
  );
}
